import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineUpload, HiOutlineTrash, HiOutlineCloud,
  HiOutlineRefresh, HiOutlineExclamation, HiOutlineCube,
  HiOutlineKey, HiOutlineShieldCheck,
} from 'react-icons/hi';
import { getGcpProjects, uploadServiceAccount, addGcpProjectByADC, deleteGcpProject } from '../services/api';
import './GcpProjects.css';

function GcpProjects({ onProjectsChanged, products = [] }) {
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [addMode, setAddMode] = useState('upload'); // 'upload' or 'adc'
  const [projectName, setProjectName] = useState('');
  const [adcProjectId, setAdcProjectId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await getGcpProjects();
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to fetch projects');
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('serviceAccount', file);
      if (projectName.trim()) {
        formData.append('projectName', projectName.trim());
      }

      await uploadServiceAccount(formData);
      toast.success('GCP project added successfully');
      setProjectName('');
      setShowUpload(false);
      fetchProjects();
      onProjectsChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload service account');
    }
    setUploading(false);
  };

  const handleAddByADC = async () => {
    if (!adcProjectId.trim()) {
      toast.error('GCP Project ID is required');
      return;
    }
    setUploading(true);
    try {
      await addGcpProjectByADC(adcProjectId.trim(), projectName.trim() || undefined);
      toast.success('GCP project added (ADC)');
      setAdcProjectId('');
      setProjectName('');
      setShowUpload(false);
      fetchProjects();
      onProjectsChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add project');
    }
    setUploading(false);
  };

  const openDeleteConfirm = (project) => {
    const affected = products.filter((p) =>
      p.cloudRuns?.some((cr) => cr.gcpProjectId === project._id)
    );
    setDeleteConfirm({ project, affectedProducts: affected });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await deleteGcpProject(deleteConfirm.project._id);
      toast.success('Project deleted');
      setDeleteConfirm(null);
      fetchProjects();
      onProjectsChanged();
    } catch (err) {
      toast.error('Failed to delete project');
    }
    setDeleting(false);
  };

  return (
    <div className="gcp-projects-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h1>GCP Projects</h1>
            <p>Manage your Google Cloud projects and service accounts</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={fetchProjects}>
              <HiOutlineRefresh size={16} /> Refresh
            </button>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <HiOutlineUpload size={16} /> Add Project
            </button>
          </div>
        </div>
      </div>

      {showUpload && (
        <div className="upload-section card">
          <h3>Add GCP Project</h3>
          <div className="add-mode-tabs">
            <button
              className={`tab-btn ${addMode === 'adc' ? 'active' : ''}`}
              onClick={() => setAddMode('adc')}
            >
              <HiOutlineShieldCheck size={16} /> ADC (Cloud Run)
            </button>
            <button
              className={`tab-btn ${addMode === 'upload' ? 'active' : ''}`}
              onClick={() => setAddMode('upload')}
            >
              <HiOutlineKey size={16} /> Service Account Key
            </button>
          </div>

          {addMode === 'adc' ? (
            <>
              <p className="upload-desc">
                Use Application Default Credentials — ideal for Cloud Run deployments where the service account is attached to the revision.
              </p>
              <div className="form-group">
                <label>GCP Project ID *</label>
                <input
                  type="text"
                  placeholder="e.g., my-project-123456"
                  value={adcProjectId}
                  onChange={(e) => setAdcProjectId(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Display Name (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., My Production Project"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowUpload(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddByADC} disabled={uploading}>
                  {uploading ? 'Adding...' : 'Add Project'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="upload-desc">
                Upload a service account JSON key file to connect to your GCP project.
              </p>
              <div className="form-group">
                <label>Project Display Name (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., My Production Project"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
              </div>
              <div className="upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  id="sa-upload"
                  hidden
                />
                <label htmlFor="sa-upload" className="upload-label">
                  <HiOutlineUpload size={28} />
                  <span>{uploading ? 'Uploading...' : 'Click to upload service account JSON'}</span>
                  <span className="upload-hint">Only .json files accepted</span>
                </label>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowUpload(false)} style={{ marginTop: 12 }}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="empty-state">
          <HiOutlineCloud size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3>No GCP Projects</h3>
          <p>Upload a service account JSON file to get started</p>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            <HiOutlineUpload size={16} /> Add Project
          </button>
        </div>
      ) : (
        <div className="card-grid">
          {projects.map((project) => (
            <div key={project._id} className="card project-card">
              <div className="project-card-header">
                <div className="project-icon">
                  <HiOutlineCloud size={22} />
                </div>
                <div className="project-info">
                  <h3>{project.projectName}</h3>
                  <span className="project-id">{project.projectId}</span>
                </div>
                <button
                  className="btn btn-danger btn-sm icon-btn"
                  onClick={() => openDeleteConfirm(project)}
                  title="Delete project"
                >
                  <HiOutlineTrash size={16} />
                </button>
              </div>
              <div className="project-card-body">
                <div className="project-status">
                  <span className={`status-badge ${project.status}`}>
                    <span className="status-dot"></span>
                    {project.status}
                  </span>
                  {project.authMode === 'adc' && (
                    <span className="auth-badge adc" title="Using Application Default Credentials">
                      <HiOutlineShieldCheck size={12} /> ADC
                    </span>
                  )}
                  {project.authMode === 'service-account' && (
                    <span className="auth-badge service-account" title="Using Service Account Key">
                      <HiOutlineKey size={12} /> SA Key
                    </span>
                  )}
                </div>
                <span className="card-date">
                  Added {new Date(project.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon danger">
              <HiOutlineTrash size={28} />
            </div>
            <h3>Delete GCP Project</h3>
            <p>
              Are you sure you want to delete{' '}
              <strong>{deleteConfirm.project.projectName}</strong>{' '}
              <span style={{ opacity: 0.6 }}>({deleteConfirm.project.projectId})</span>?
            </p>

            {deleteConfirm.affectedProducts.length > 0 && (
              <div className="confirm-warning">
                <div className="confirm-warning-header">
                  <HiOutlineExclamation size={18} />
                  <span>The following products depend on this project and will stop working:</span>
                </div>
                <ul className="confirm-affected-list">
                  {deleteConfirm.affectedProducts.map((p) => (
                    <li key={p._id}>
                      <HiOutlineCube size={14} />
                      <span>{p.name}</span>
                      <span className="confirm-affected-count">
                        {p.cloudRuns.filter((cr) => cr.gcpProjectId === deleteConfirm.project._id).length} service(s)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="confirm-sub">
              This will remove the service account and disconnect all services. Cloud Run services on GCP will not be affected.
            </p>

            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GcpProjects;
